import { configureStore, createListenerMiddleware, addListener as addListenerSource, TypedAddListener, StoreEnhancer, Middleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import { useSelector as useSelectorRedux, useDispatch as useDispatchRedux, TypedUseSelectorHook } from 'react-redux';
import { createLogger } from 'redux-logger';
import { createReduxEnhancer } from '@sentry/react';

import { api as apiMain } from '#adapter/api/main';
import { api as apiCampaigns } from '#adapter/api/campaigns';
import { api as apiCallCenter } from '#adapter/api/call-center';
import { api as separateApiAuth } from '#adapter/api/auth';
import { env } from '#helper/env';
import { separateApiCampaigns } from '#adapter/api/campaigns/separate-api-campaign';
import { separateApiPortalGate } from '#adapter/api/portal-gate/separate-api-portal-gate';

import { sliderMenu } from '#component/slider/slice';
import { slice as sliceUser } from '#component/user/slice';
import { notificationsSlice } from '#component/header/components/notification/slice';
import { wordTabs } from '#component/keywords/slices/words-stats';
import { searchWordTabs } from '#component/keywords/slices/search-words-stats';
import { updateCampaignStatus } from '#route/campaigns/components/list-item/components/dropdown-menu/slice';
import { budgetCampaignSlice, editCampaignSlice, activeGroupsSlice } from '#route/campaigns/edit/slice';
import { getMediaQuery } from '#component/media-query/slice';
import { listItemsSlice } from '#route/campaigns/create/components/list-items/slice';
import { campaignsListSlice } from '#route/campaigns/list-my-campaigns/slice';
import { autoListItemsSlice } from '#route/campaigns/create/pages/auto/components/section-goods/slice';
import { hasBiggestBetMiddleware } from '#route/campaigns/create/pages/auto/components/section-goods/middleware';

export const listenerMiddleware = createListenerMiddleware();

const rootReducer = {
    [apiMain.reducerPath]              : apiMain.reducer,
    [apiCampaigns.reducerPath]         : apiCampaigns.reducer,
    [separateApiCampaigns.reducerPath] : separateApiCampaigns.reducer,
    [apiCallCenter.reducerPath]        : apiCallCenter.reducer,
    [separateApiAuth.reducerPath]      : separateApiAuth.reducer,
    [sliderMenu.name]                  : sliderMenu.reducer,
    [notificationsSlice.name]          : notificationsSlice.reducer,
    [updateCampaignStatus.name]        : updateCampaignStatus.reducer,
    [editCampaignSlice.name]           : editCampaignSlice.reducer,
    [budgetCampaignSlice.name]         : budgetCampaignSlice.reducer,
    [sliceUser.name]                   : sliceUser.reducer,
    [getMediaQuery.name]               : getMediaQuery.reducer,
    [wordTabs.name]                    : wordTabs.reducer,
    [searchWordTabs.name]              : searchWordTabs.reducer,
    [listItemsSlice.name]              : listItemsSlice.reducer,
    [autoListItemsSlice.name]          : autoListItemsSlice.reducer,
    [activeGroupsSlice.name]           : activeGroupsSlice.reducer,
    [separateApiPortalGate.reducerPath]: separateApiPortalGate.reducer,
    [campaignsListSlice.name]          : campaignsListSlice.reducer
};

export const store = configureStore({
    devTools  : import.meta.env.DEV,
    reducer   : rootReducer,
    middleware: (getDefaultMiddleware) => {
        const middleware = getDefaultMiddleware().concat(
            listenerMiddleware.middleware,
            apiMain.middleware,
            apiCampaigns.middleware,
            separateApiCampaigns.middleware,
            separateApiPortalGate.middleware,
            apiCallCenter.middleware,
            separateApiAuth.middleware,
            hasBiggestBetMiddleware
        );

        if(import.meta.env.DEV) {
            middleware.push(
                createLogger({
                    duration : env(import.meta.env.APP_REDUX_LOGGER_DURATION),
                    collapsed: env(import.meta.env.APP_REDUX_LOGGER_COLLAPSED)
                }) as Middleware<unknown, unknown>
            );
        }

        return middleware;
    },
    enhancers: (enhancers) => {
        enhancers.push(createReduxEnhancer({
            actionTransformer: (action) => {
                if(action.type.includes('api/')) {
                    return null;
                }

                return action;
            },
            attachReduxState: false
        }) as StoreEnhancer<never, never>);

        return enhancers;
    }
});

export const setupStore = (preloadedState?: Partial<TStore>) => {
    return configureStore({
        reducer   : rootReducer,
        preloadedState,
        middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false, logger: false })
            .concat(
                listenerMiddleware.middleware,
                apiMain.middleware,
                apiCampaigns.middleware,
                separateApiCampaigns.middleware,
                separateApiPortalGate.middleware,
                apiCallCenter.middleware,
                separateApiAuth.middleware,
                hasBiggestBetMiddleware
            )
    });
};

setupListeners(store.dispatch);

export type TStore = ReturnType<typeof store.getState>;
export type TDispatch = typeof store.dispatch;

export const useDispatch: () => TDispatch = useDispatchRedux;
export const useSelector = useSelectorRedux as TypedUseSelectorHook<TStore>;
export const addListener = addListenerSource as TypedAddListener<TDispatch>;

export default store;
